import { isDefined } from "@prismicio/editor-support/Type"
import {
  AnimatedList,
  BackgroundIcon,
  type BackgroundIconProps,
  Box,
  type IconName,
  SelectCard,
  Text,
} from "@prismicio/editor-ui"
import type { MouseEventHandler, PropsWithChildren, ReactNode } from "react"
import { Link } from "react-router-dom"

export interface Section {
  id: "unclassified" | "published"
  header: string
  title: string
  subtitle?: string
  selected: boolean
  onSelect: MouseEventHandler<HTMLButtonElement>
}

interface ReleaseSection {
  id: "release"
  header: string
  items: {
    title?: string
    date?: string
    subtitle: string
    selected: boolean
    releaseId?: string
    onSelect: MouseEventHandler<HTMLButtonElement>
  }[]
}

interface MigrationReleaseSection {
  id: "migrationRelease"
  header: string
  items: {
    title?: string
    date?: string
    subtitle: string
    selected: boolean
    releaseId?: string
    onSelect: MouseEventHandler<HTMLButtonElement>
  }[]
}

interface LocalSection {
  id: "local"
  header: string
  title: string
  selected: boolean
  onSelect: MouseEventHandler<HTMLButtonElement>
}

export interface DocumentVersionsProps {
  local?: LocalSection
  unclassified?: Section
  published?: Section
  release?: ReleaseSection
  migrationRelease?: MigrationReleaseSection
}

export function DocumentVersions(props: DocumentVersionsProps) {
  const { local, unclassified, published, release, migrationRelease } = props

  const items = [
    local,
    unclassified,
    published,
    release,
    migrationRelease,
  ].filter(isDefined)

  return (
    <AnimatedList
      ariaLabel="Document Versions"
      items={items}
      getItemKey={(item) => item.id}
      gap={16}
      animationDuration={250}
    >
      {(item) => {
        if (item.id === "local")
          return (
            <Section>
              <Header item={item} />
              <SelectCard {...item}>
                <Text variant="bold" color="grey12" align="start">
                  {item.title}
                </Text>
              </SelectCard>
            </Section>
          )

        if (item.id === "unclassified")
          return (
            <Section>
              <Header item={item} />
              <SelectCard asChild {...item}>
                <VersionLink status="unclassified">
                  <Text variant="bold" color="grey12" align="start">
                    {item.title}
                  </Text>
                  <Text variant="normal" color="grey11" align="start">
                    {item.subtitle}
                  </Text>
                </VersionLink>
              </SelectCard>
            </Section>
          )

        if (item.id === "published")
          return (
            <Section>
              <Header item={item} />
              <SelectCard asChild {...item}>
                <VersionLink status="published">
                  <Text variant="bold" color="grey12" align="start">
                    {item.title}
                  </Text>
                  <Text variant="normal" color="grey11" align="start">
                    {item.subtitle}
                  </Text>
                </VersionLink>
              </SelectCard>
            </Section>
          )

        if (item.id === "release")
          return (
            <Section>
              <Header item={item} />
              {release?.items.map((item, index) => (
                <SelectCard
                  key={`${index}${item.title ?? ""}`}
                  asChild
                  {...item}
                >
                  <VersionLink status="release" releaseId={item.releaseId}>
                    {getReleaseTitle(item.title, item.date)}
                    <Text variant="normal" color="grey11" align="start">
                      {item.subtitle}
                    </Text>
                  </VersionLink>
                </SelectCard>
              ))}
            </Section>
          )

        if (item.id === "migrationRelease")
          return (
            <Section>
              <Header item={item} />
              {migrationRelease?.items.map((item, index) => (
                <SelectCard
                  key={`${index}${item.title ?? ""}`}
                  asChild
                  {...item}
                >
                  <VersionLink status="release" releaseId={item.releaseId}>
                    {getReleaseTitle(item.title, item.date)}
                    <Text variant="normal" color="grey11" align="start">
                      {item.subtitle}
                    </Text>
                  </VersionLink>
                </SelectCard>
              ))}
            </Section>
          )

        return null
      }}
    </AnimatedList>
  )
}

interface VersionLinkProps {
  children: ReactNode
  status: "unclassified" | "published" | "release"
  releaseId?: string
}

function VersionLink(props: VersionLinkProps) {
  const { children, status, releaseId, ...asChildProps } = props
  return (
    <Link
      {...asChildProps}
      to={{
        pathname: ".",
        search: new URLSearchParams({
          s: status,
          ...(releaseId ? { r: releaseId } : {}),
        }).toString(),
      }}
      replace
    >
      {children}
    </Link>
  )
}

function Section(props: PropsWithChildren) {
  const { children } = props

  return (
    <Box flexDirection="column" gap={8}>
      {children}
    </Box>
  )
}

type Item = LocalSection | Section | ReleaseSection | MigrationReleaseSection

interface HeaderProps {
  item: Item
}

function Header(props: HeaderProps) {
  const { item } = props

  const { name, color } = getHeaderIcon(item)

  return (
    <Box display="flex" alignItems="center" gap={4}>
      <BackgroundIcon
        name={name}
        color={color}
        size="extraSmall"
        iconSize="medium"
        radius={6}
      />

      <Text variant="emphasized" color="grey12">
        {item.header}
      </Text>
    </Box>
  )
}

interface HeaderIcon {
  name: IconName
  color: BackgroundIconProps["color"]
}

function getHeaderIcon(item: Item): HeaderIcon {
  switch (item.id) {
    case "local":
    case "unclassified":
      return {
        name: "edit",
        color: "amber",
      }
    case "published":
      return {
        name: "public",
        color: "green",
      }
    case "release":
      return {
        name: "schedule",
        color: "purple",
      }
    case "migrationRelease":
      return {
        name: "migrationRelease",
        color: "indigo",
      }
  }
}

function getReleaseTitle(label?: string, timeStamp?: string) {
  if (!timeStamp && !label) return undefined

  if (!timeStamp) return <TitleWithLabel label={label} />

  if (!label) return <TitleWithTimestamp timeStamp={timeStamp} />

  return <TitleWithTimestampAndLabel timeStamp={timeStamp} label={label} />
}

interface TitleWithLabelProps {
  label: string | undefined
}
function TitleWithLabel(props: TitleWithLabelProps) {
  const { label } = props

  return (
    <Box flexDirection="column">
      <Text variant="normal" color="grey12" align="start">
        "{label}" release
      </Text>
      <Text variant="emphasized" color="grey12" align="start">
        Unscheduled
      </Text>
    </Box>
  )
}

interface TitleWithTimestampProps {
  timeStamp: string | undefined
}
function TitleWithTimestamp(props: TitleWithTimestampProps) {
  const { timeStamp } = props

  return (
    <Text variant="emphasized" color="grey12" align="start">
      {timeStamp}
    </Text>
  )
}

interface TitleWithTimestampAndLabelProps {
  label: string
  timeStamp: string
}

function TitleWithTimestampAndLabel(props: TitleWithTimestampAndLabelProps) {
  const { label, timeStamp } = props

  return (
    <Box flexDirection="column">
      <Text variant="normal" color="grey12" align="start">
        "{label}" release
      </Text>
      <Text variant="emphasized" color="grey12" align="start">
        {timeStamp}
      </Text>
    </Box>
  )
}
